export type AppRole = "admin" | "manager" | "cashier";
export type StoreAccessRole = "owner" | "admin" | "manager" | "cashier" | "viewer";

export const STORE_CONTEXT_REFRESH_EVENT = "storeledger:stores-updated";

type UserLike = {
  invited_at?: string | null;
  app_metadata?: Record<string, unknown>;
  user_metadata?: Record<string, unknown>;
} | null | undefined;

export type StoreOnboardingState = {
  hasStoreAccess: boolean;
  canCreateFirstStore: boolean;
  shouldAskAdminForStoreAccess: boolean;
  status: "has_store_access" | "needs_first_store" | "needs_store_assignment" | "global_setup_access";
  setupRoles: StoreAccessRole[];
};

export function isInvitedUser(user: UserLike): boolean {
  if (!user) return false;
  return Boolean(
    user.invited_at ||
    user.app_metadata?.invited === true ||
    user.app_metadata?.invitation === true ||
    user.user_metadata?.invited === true,
  );
}

function hasOnlyLegacyDefaultCashierRole(roles: AppRole[]): boolean {
  return roles.length === 0 || roles.every((role) => role === "cashier");
}

export function resolveStoreOnboardingState({
  user,
  roles,
  storeAccessRoles,
}: {
  user: UserLike;
  roles: AppRole[];
  storeAccessRoles: StoreAccessRole[];
}): StoreOnboardingState {
  const setupRoles = storeAccessRoles.filter((role) =>
    role === "owner" || role === "admin" || role === "manager"
  );
  const hasStoreAccess = storeAccessRoles.length > 0;
  const isGlobalSetupRole = roles.includes("admin") || roles.includes("manager");
  const canCreateFirstStore =
    Boolean(user) &&
    !hasStoreAccess &&
    !isInvitedUser(user) &&
    hasOnlyLegacyDefaultCashierRole(roles);
  const status: StoreOnboardingState["status"] = hasStoreAccess
    ? "has_store_access"
    : canCreateFirstStore
      ? "needs_first_store"
      : isGlobalSetupRole
        ? "global_setup_access"
        : "needs_store_assignment";

  return {
    hasStoreAccess,
    canCreateFirstStore,
    shouldAskAdminForStoreAccess:
      Boolean(user) && !hasStoreAccess && !canCreateFirstStore && !isGlobalSetupRole,
    status,
    setupRoles,
  };
}

export function notifyStoreContextChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(STORE_CONTEXT_REFRESH_EVENT));
  }
}
